/**
 * Raw-data view of the trend pipeline. Shows what's actually trending per
 * source (TikTok / Google Trends / News / YouTube / Reddit), before any
 * Gemini filtering. Lives alongside "Trends For You" as a peer tab on
 * TrendsPage — this is the source-of-truth surface.
 *
 * Filters inherited from the parent page: country + free-text query.
 * Filters NOT carried over: category/status — those are Gemini-synthesized
 * attributes that don't apply to raw rankings.
 */
import { useMemo, useState } from 'react';
import { ExternalLink, Flame, Hash, Newspaper, Video, MessageCircle, TrendingUp } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/components/ui/utils';
import type {
  TrendRankings,
  TrendRankingsTikTokHashtag,
  TrendRankingsGoogleTrendsItem,
  TrendRankingsNewsItem,
  TrendRankingsYouTubeItem,
  TrendRankingsRedditItem,
} from '@/utils/trend';

/** Short number formatter: 12,430,000 → "12.4M". Used in the Creative Center-style
 *  rankings rows where horizontal space is tight. */
function compactNumber(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(Math.round(n));
}

export interface TrendRankingsViewProps {
  rankings: TrendRankings;
  /** Country filter ('all', 'global', or ISO code). Applies to TikTok + Google Trends panels. */
  geo: string;
  /** Free-text filter — case-insensitive AND-match against titles / hashtags / keywords. */
  query: string;
}

const DEFAULT_VISIBLE = 10;

function matchQuery(haystack: (string | null | undefined)[], query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const tokens = q.split(/\s+/).filter(Boolean);
  const h = haystack.filter(Boolean).join(' \n ').toLowerCase();
  return tokens.every((t) => h.includes(t));
}

export function TrendRankingsView({ rankings, geo, query }: TrendRankingsViewProps) {
  const filtered = useMemo(() => {
    const geoPass = (country: string | null | undefined) => {
      if (!geo || geo === 'all' || geo === 'global') return true;
      return !!country && country.toUpperCase() === geo.toUpperCase();
    };
    return {
      tiktok_hashtags: rankings.tiktok_hashtags
        .filter((h) => geoPass(h.country))
        .filter((h) => matchQuery([h.hashtag, h.country, ...(h.top_country_list ?? [])], query)),
      google_trends_now: rankings.google_trends_now
        .filter((g) => geoPass(g.country))
        .filter((g) => matchQuery([g.keyword, g.sample_headline, g.country], query)),
      google_news: rankings.google_news.filter((n) =>
        matchQuery([n.title, n.keyword, n.source], query)
      ),
      youtube: rankings.youtube.filter((v) =>
        matchQuery([v.title, v.keyword, v.channel], query)
      ),
      reddit: rankings.reddit.filter((r) =>
        matchQuery([r.title, r.subreddit], query)
      ),
    };
  }, [rankings, geo, query]);

  const totalCount =
    filtered.tiktok_hashtags.length +
    filtered.google_trends_now.length +
    filtered.google_news.length +
    filtered.youtube.length +
    filtered.reddit.length;

  if (totalCount === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-10 text-center">
        <h3 className="text-[15px] font-semibold text-[#344054]">No ranking data for these filters</h3>
        <p className="mt-2 text-[13px] text-gray-500">
          Try a broader country or clear the search term.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <TikTokPanel items={filtered.tiktok_hashtags} />
      <GoogleTrendsPanel items={filtered.google_trends_now} />
      <NewsPanel items={filtered.google_news} />
      <YouTubePanel items={filtered.youtube} />
      <RedditPanel items={filtered.reddit} />
    </div>
  );
}

function PanelShell({
  title,
  subtitle,
  icon,
  total,
  children,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  total: number;
  children: React.ReactNode;
}) {
  if (total === 0) return null;
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#f3ecff]">
            {icon}
          </div>
          <div>
            <h3 className="text-[14px] font-bold text-[#101828]">{title}</h3>
            <p className="text-[11px] text-gray-500">{subtitle}</p>
          </div>
        </div>
        <Badge className="border-0 bg-gray-100 text-[10px] font-semibold text-gray-700">
          {total} {total === 1 ? 'item' : 'items'}
        </Badge>
      </div>
      {children}
    </div>
  );
}

function Expandable({ total, defaultVisible = DEFAULT_VISIBLE, children }: {
  total: number;
  defaultVisible?: number;
  children: (limit: number) => React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const limit = expanded ? total : Math.min(defaultVisible, total);
  return (
    <>
      {children(limit)}
      {total > defaultVisible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-[12px] font-medium text-[#be43ff] hover:underline"
        >
          {expanded ? 'Show less' : `Show all ${total}`}
        </button>
      )}
    </>
  );
}

/**
 * TikTok hashtag rankings styled after TikTok Creative Center's Popular Hashtags
 * page (ads.tiktok.com/business/creativecenter/inspiration/popular/hashtag).
 *
 * Layout:
 *  - Top featured row: first 3 hashtags as large cards with big rank number +
 *    sparkline + stats, each scaled for scannability.
 *  - Main list: numbered rows with rank, hashtag, posts, rank-change badge,
 *    inline sparkline, velocity pill, and a country tag.
 *
 * Sort: by global rank_position when available (that's what Creative Center shows),
 * falling back to velocity for hashtags without a server-side rank number.
 */
function TikTokPanel({ items }: { items: TrendRankingsTikTokHashtag[] }) {
  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const ra = a.rank_position ?? 9999;
      const rb = b.rank_position ?? 9999;
      if (ra !== rb) return ra - rb;
      return b.velocity_ratio - a.velocity_ratio;
    });
  }, [items]);
  if (sorted.length === 0) return null;
  const featured = sorted.slice(0, 3);
  const rest = sorted.slice(3);
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#f3ecff]">
            <Hash className="h-4 w-4 text-[#be43ff]" />
          </div>
          <div>
            <h3 className="text-[14px] font-bold text-[#101828]">TikTok · Popular hashtags</h3>
            <p className="text-[11px] text-gray-500">
              Ranked by TikTok's 7-day position: live from Creative Center Discovery API.
            </p>
          </div>
        </div>
        <Badge className="border-0 bg-gray-100 text-[10px] font-semibold text-gray-700">
          {sorted.length} tracked
        </Badge>
      </div>

      {/* Featured trio — mirrors Creative Center's hero cards. */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {featured.map((h, i) => (
          <HashtagFeaturedCard key={`${h.hashtag}-${h.country}`} h={h} idx={i} />
        ))}
      </div>

      {/* Main numbered list — Creative Center's "next 50". */}
      {rest.length > 0 && (
        <Expandable total={rest.length} defaultVisible={12}>
          {(limit) => (
            <div className="overflow-hidden rounded-xl border border-gray-100">
              <table className="w-full border-collapse text-[12px]">
                <thead className="bg-gray-50 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="w-[60px] py-2 pl-4 text-left">Rank</th>
                    <th className="py-2 text-left">Hashtag</th>
                    <th className="hidden py-2 text-left sm:table-cell">Country</th>
                    <th className="py-2 text-right">Posts</th>
                    <th className="py-2 text-right">Change</th>
                    <th className="w-[120px] py-2 pr-4 text-right">7-day trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rest.slice(0, limit).map((h, i) => (
                    <HashtagRow key={`${h.hashtag}-${h.country}`} h={h} rank={i + 4} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Expandable>
      )}
    </div>
  );
}

function HashtagFeaturedCard({ h, idx }: { h: TrendRankingsTikTokHashtag; idx: number }) {
  const rankLabel = h.rank_position != null ? String(h.rank_position).padStart(2, '0') : String(idx + 1).padStart(2, '0');
  const trendPct = Math.round((h.velocity_ratio - 1) * 100);
  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-[#faf5ff] p-4 transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Rank
          </div>
          <div className="mt-0.5 text-[36px] font-bold leading-none text-[#2e024c]">
            {rankLabel}
          </div>
        </div>
        <Badge className="border-0 bg-sky-100 text-[10px] font-semibold text-sky-700">
          {h.country}
        </Badge>
      </div>
      <div className="mt-3 truncate text-[16px] font-bold text-[#101828]">
        #{h.hashtag}
      </div>
      <div className="mt-1 flex items-baseline gap-2 text-[11px] text-gray-500">
        <span className="font-semibold text-gray-900">{compactNumber(h.posts)}</span>
        <span>posts</span>
        {h.rank_change !== 0 && (
          <span
            className={cn(
              'ml-auto inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-semibold',
              h.rank_change > 0
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-rose-100 text-rose-700'
            )}
          >
            {h.rank_change > 0 ? '▲' : '▼'} {Math.abs(h.rank_change)}
          </span>
        )}
      </div>
      {h.views_daily_series.length > 1 && (
        <div className="mt-3 h-[44px] w-full">
          <Sparkline series={h.views_daily_series} />
        </div>
      )}
      <div className="mt-2 flex items-center gap-2 text-[10px] text-gray-500">
        <Flame className="h-3 w-3 text-orange-500" />
        <span>Peak {compactNumber(h.views_7d_peak)} daily views · vel {trendPct >= 0 ? '+' : ''}{trendPct}%</span>
      </div>
    </div>
  );
}

function HashtagRow({ h, rank }: { h: TrendRankingsTikTokHashtag; rank: number }) {
  const rankLabel = h.rank_position != null ? String(h.rank_position).padStart(2, '0') : String(rank).padStart(2, '0');
  return (
    <tr className="transition-colors hover:bg-[#faf5ff]">
      <td className="py-3 pl-4 font-mono text-[13px] font-semibold text-gray-500">
        {rankLabel}
      </td>
      <td className="py-3">
        <div className="font-semibold text-[#2e024c]">#{h.hashtag}</div>
        <div className="text-[10px] text-gray-400">
          {compactNumber(h.posts_global_lifetime)} lifetime
        </div>
      </td>
      <td className="hidden py-3 sm:table-cell">
        <Badge className="border-0 bg-gray-100 text-[10px] font-semibold text-gray-700">
          {h.country}
        </Badge>
      </td>
      <td className="py-3 text-right">
        <div className="font-semibold text-gray-900">{compactNumber(h.posts)}</div>
      </td>
      <td className="py-3 text-right">
        {h.rank_change !== 0 ? (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-semibold',
              h.rank_change > 0
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-rose-100 text-rose-700'
            )}
          >
            {h.rank_change > 0 ? '▲' : '▼'} {Math.abs(h.rank_change)}
          </span>
        ) : (
          <span className="text-[10px] text-gray-400">—</span>
        )}
      </td>
      <td className="py-3 pr-4">
        <div className="ml-auto h-[28px] w-[100px]">
          {h.views_daily_series.length > 1 ? (
            <Sparkline series={h.views_daily_series} />
          ) : (
            <span className="text-[10px] text-gray-300">—</span>
          )}
        </div>
      </td>
    </tr>
  );
}

function Sparkline({ series }: { series: number[] }) {
  const data = series.map((v, i) => ({ i, v }));
  return (
    <ResponsiveContainer>
      <AreaChart data={data}>
        <defs>
          <linearGradient id={`spark-rk-${series.length}-${series[0]}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#be43ff" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#be43ff" stopOpacity={0.04} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="v"
          stroke="#be43ff"
          strokeWidth={1.5}
          fill={`url(#spark-rk-${series.length}-${series[0]})`}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function GoogleTrendsPanel({ items }: { items: TrendRankingsGoogleTrendsItem[] }) {
  return (
    <PanelShell
      title="Google Trends · trending now"
      subtitle="Live daily search surges per market, via Google Trends RSS."
      icon={<TrendingUp className="h-4 w-4 text-[#be43ff]" />}
      total={items.length}
    >
      <Expandable total={items.length}>
        {(limit) => (
          <ul className="flex flex-col divide-y divide-gray-100">
            {items.slice(0, limit).map((g, i) => (
              <li key={`${g.country}-${g.keyword}-${i}`} className="flex flex-wrap items-center gap-2 py-2 text-[12px]">
                <Badge className="border-0 bg-sky-100 text-[10px] text-sky-700">{g.country}</Badge>
                <span className="font-semibold text-[#101828]">{g.keyword}</span>
                {g.sample_headline && (
                  <span className="truncate text-[11px] italic text-gray-500">{g.sample_headline}</span>
                )}
                <span className="ml-auto text-[11px] text-gray-500">
                  {g.traffic > 0 ? `${g.traffic.toLocaleString()}+ searches` : '—'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Expandable>
    </PanelShell>
  );
}

function NewsPanel({ items }: { items: TrendRankingsNewsItem[] }) {
  return (
    <PanelShell
      title="Google News · past week"
      subtitle="News hits per seed keyword: filtered to the last 7 days."
      icon={<Newspaper className="h-4 w-4 text-[#be43ff]" />}
      total={items.length}
    >
      <Expandable total={items.length}>
        {(limit) => (
          <ul className="flex flex-col divide-y divide-gray-100">
            {items.slice(0, limit).map((n, i) => (
              <li key={`${n.url || n.title}-${i}`} className="flex flex-col gap-1 py-2 text-[12px]">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="border-0 bg-gray-100 text-[10px] font-medium text-gray-700">{n.keyword}</Badge>
                  {n.source && (
                    <span className="text-[11px] font-medium text-gray-500">{n.source}</span>
                  )}
                  {n.published && (
                    <span className="text-[11px] text-gray-400">· {n.published}</span>
                  )}
                </div>
                <a
                  href={n.url ?? '#'}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex items-start gap-1.5 font-medium text-[#101828] hover:text-[#be43ff]"
                >
                  <span className="line-clamp-2">{n.title}</span>
                  {n.url && <ExternalLink className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-400" />}
                </a>
              </li>
            ))}
          </ul>
        )}
      </Expandable>
    </PanelShell>
  );
}

function YouTubePanel({ items }: { items: TrendRankingsYouTubeItem[] }) {
  return (
    <PanelShell
      title="YouTube · past week"
      subtitle="Video results per seed keyword: filtered to the last 7 days."
      icon={<Video className="h-4 w-4 text-[#be43ff]" />}
      total={items.length}
    >
      <Expandable total={items.length}>
        {(limit) => (
          <ul className="flex flex-col divide-y divide-gray-100">
            {items.slice(0, limit).map((v, i) => (
              <li key={`${v.url || v.title}-${i}`} className="flex flex-col gap-1 py-2 text-[12px]">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="border-0 bg-gray-100 text-[10px] font-medium text-gray-700">{v.keyword}</Badge>
                  {v.channel && (
                    <span className="text-[11px] font-medium text-gray-500">{v.channel}</span>
                  )}
                  {v.duration && (
                    <span className="text-[11px] text-gray-400">· {v.duration}</span>
                  )}
                  {v.published && (
                    <span className="text-[11px] text-gray-400">· {v.published}</span>
                  )}
                </div>
                <a
                  href={v.url ?? '#'}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex items-start gap-1.5 font-medium text-[#101828] hover:text-[#be43ff]"
                >
                  <span className="line-clamp-2">{v.title}</span>
                  {v.url && <ExternalLink className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-400" />}
                </a>
              </li>
            ))}
          </ul>
        )}
      </Expandable>
    </PanelShell>
  );
}

function RedditPanel({ items }: { items: TrendRankingsRedditItem[] }) {
  return (
    <PanelShell
      title="Reddit · top hot posts"
      subtitle="Posts from the watched subreddits, ranked by score."
      icon={<MessageCircle className="h-4 w-4 text-[#be43ff]" />}
      total={items.length}
    >
      <Expandable total={items.length}>
        {(limit) => (
          <ul className="flex flex-col divide-y divide-gray-100">
            {items.slice(0, limit).map((r, i) => (
              <li key={`${r.url || r.title}-${i}`} className="flex flex-col gap-1 py-2 text-[12px]">
                <div className="flex flex-wrap items-center gap-2">
                  {r.subreddit && (
                    <Badge className="border-0 bg-orange-100 text-[10px] font-medium text-orange-700">
                      r/{r.subreddit}
                    </Badge>
                  )}
                  <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                    <Flame className="h-3 w-3 text-orange-500" />
                    {r.score.toLocaleString()}
                  </span>
                  <span className="text-[11px] text-gray-400">· {r.comments.toLocaleString()} comments</span>
                </div>
                <a
                  href={r.url ?? '#'}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex items-start gap-1.5 font-medium text-[#101828] hover:text-[#be43ff]"
                >
                  <span className="line-clamp-2">{r.title}</span>
                  {r.url && <ExternalLink className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-400" />}
                </a>
              </li>
            ))}
          </ul>
        )}
      </Expandable>
    </PanelShell>
  );
}
